import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  defaultAnimateLayoutChanges,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getMediaObjects, useGlobalState } from "senf-shared";
import type { Project } from "senf-shared/types";
import { getAlpha } from "senf-shared/utils/helpers";
import { getFileTypeLabel } from "senf-shared/utils/helpers";
import { tv } from "tailwind-variants";
import { useImageMetaData } from "../../../../hooks/useImageMetaData";
import { useOnKeyDown } from "../../../../hooks/useOnKeyDown";
import { cn } from "../../../../util/classNameConstructor";
import Button from "../../../core/button/Button";
import FileDrop from "../../../core/fileDrop/FileDrop";
import Icon from "../../../core/icon/Icon";
import InvisibleInput from "../../../core/inputs/InvisibleInput";
import TooltipProvider from "../../../core/tooltip/TooltipProvider";
import ModalButton from "../../../molecules/modalStack/ModalButton";
import { useModals } from "../../../molecules/modalStack/ModalProvider";
import type {
  Question,
  QuestionBaseProps,
} from "../../../special/survey/survey.types";
import ImagesUploadModal from "../../imageModifier/ImagesUploadModal";
import ModifyImageButtons from "../../imageModifier/ModifyImageButtons";
import { useOnDragEnd } from "../../pageBuilder/SortableSections";
import { useRandomSorting, useVerifyFiles } from "../hooks";
import { pageTypeConfig } from "../pageTypes";
import { isChecked, toggle } from "../utils";

import i18n from "senf-shared/utils/i18n";
import { ChoiceItem } from "../components/ChoiceItemWrapper";
import Textarea from "./Textarea";
const ImageChoiceStyles = tv({
  slots: {
    Outer: "flex w-full flex-col",
    Wrapper: "w-full pb-6",
    Grid: "grid w-full auto-rows-fr auto-rows-fr grid-cols-[repeat(auto-fit,minmax(var(--w-40),_1fr))] gap-3",
    Option:
      "group/option relative flex h-full w-full shrink-0 grow-0 flex-col items-start justify-start rounded-xl ring-1 ring-border-subdued bg-surface-subdued focusable",
    ImageWrapper:
      "bg-surface-highlight-subdued-hovered border-border-highlight-subdued text-action-primary-pressed flex aspect-square w-full items-center justify-center rounded-t-xl border border-solid p-0",
    Checkmark:
      "checkbox bg-action-primary-default absolute -right-2 -top-2 rounded-bl-xl rounded-tr-xl p-1.5",
    AlphaNum:
      "border-border-highlight-subdued text-text-default relative flex h-6 w-6 shrink-0 flex-col items-center justify-center gap-2 rounded-md border bg-white",
    RemoveItemButton: "opacity-0 group-hover/option:opacity-100 bg-white",
  },
  variants: {
    checked: {
      true: {
        Option: "ring-[3px]",
        AlphaNum:
          "bg-action-primary-default border-action-primary-default text-text-on-primary",
      },
      false: {
        Checkmark: "opacity-0",
      },
    },
    disabled: {
      true: {
        Option: "pointer-events-none !opacity-50",
      },
    },
    hoverable: {
      true: {
        Option:
          "hover:bg-surface-highlight-subdued-hovered hover:ring-border-highlight-default cursor-pointer",
      },
    },
  },
});

interface ImageChoiceProps extends Question, QuestionBaseProps {
  project: Project;
  surveyId: string;
}

interface ImageChoiceItemProps extends ImageChoiceProps {
  id: string;
  rowIndex?: number;
  option?: NonNullable<Question["options"]>[number];
  options?: Question["options"];
  onClick?: (uid?: string) => void;
  handleFileChange?: (files: FileList | File[], uid: string) => void;
  loading?: boolean;
  checked?: string | string[];
  type?: "radio" | "checkbox";
  draggable?: boolean;
}

export const AlphaNum = ({
  rowIndex,
  checked,
  className,
}: {
  rowIndex: number;
  checked: boolean;
  className?: string;
}) => {
  const { AlphaNum } = ImageChoiceStyles({
    checked,
  });
  return (
    <div className={AlphaNum({ className })}>
      <p className="font-bold whitespace-nowrap">{getAlpha(rowIndex || 0)}</p>
    </div>
  );
};

const SimpleImageChoiceItem = ({ option, onChange, value, question }) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;
  const { metadata: imageMetadata, fetchMetadata } = useImageMetaData({
    images: useMemo(
      () => [{ url: option?.url, alt: option?.altText }],
      [option?.url, option?.altText],
    ),
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchMetadata();
  }, []);

  const augmentedOption = useMemo(() => {
    return {
      ...option,
      altText:
        translation?.[option?.altText] ||
        option?.altText ||
        imageMetadata[option.url]?.customMetadata?.alt ||
        `${t("global:image")}: ${translation?.[option.value] || option.value}`,
      copyright:
        option?.copyright ||
        imageMetadata[option.url]?.customMetadata?.copyright,
    };
  }, [option, translation, imageMetadata, t]);

  return (
    <ChoiceItem
      question={question}
      option={augmentedOption}
      column={question.columns?.[0]}
      value={value}
      onChange={onChange}
    />
  );
};

const ImageChoiceItem = ({
  project,
  surveyId,
  id,
  option,
  mode,
  rowIndex,
  onClick,
  removeItem,
  handleFileChange,
  loading,
  draggable,
  ...props
}: ImageChoiceItemProps) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const [translation] = useGlobalState().translationsState;
  const { metadata: imageMetadata, fetchMetadata } = useImageMetaData({
    images: useMemo(
      () => [{ url: option?.url, alt: option?.altText }],
      [option?.url, option?.altText],
    ),
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchMetadata();
  }, []);

  const {
    attributes,
    listeners,
    isDragging,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    disabled: draggable === false || mode !== "edit" || !option?.uid,
    animateLayoutChanges: (args) => {
      return defaultAnimateLayoutChanges({ ...args, wasDragging: true });
    },
  });
  const inputRef = useRef<HTMLInputElement>(null);

  const style = {
    opacity: isDragging && (rowIndex || Number.POSITIVE_INFINITY) > -1 ? 0 : 1,
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const checked =
    !!option?.uid &&
    props.checked !== undefined &&
    props.type !== undefined &&
    isChecked(props.checked, option.uid, props.type);

  const disabled = !!(
    props.multipleChoice &&
    props.maxChoices &&
    (props.checked?.length || 0) >= props.maxChoices &&
    !checked
  );

  const removable =
    (props.options?.length || 0) >
    (pageTypeConfig[props.qtype]?.defaults?.minOptions || 0);

  const { Option, ImageWrapper, RemoveItemButton } = ImageChoiceStyles({
    hoverable: onClick !== undefined,
    disabled,
    checked,
  });
  const hasConditions = props.conditions?.some((e) => e.type !== "none");

  const handleKeyDown = (e) => {
    if (mode === "display" && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      if (onClick) onClick(option?.uid);
    }
  };
  const fileTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

  return (
    <TooltipProvider
      critical={!!disabled}
      disabled={disabled}
      primaryContent={t("survey:disabledMessages.radio.maxChoices", {
        count: props.maxChoices,
      })}
      noTooltip={!disabled}
      className={cn(disabled && "cursor-not-allowed select-none")}
      onClick={
        onClick && (option || id === "add")
          ? () => onClick(option?.uid)
          : undefined
      }
    >
      <div
        className={Option()}
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {option && (
          <div className="absolute end-2 top-2 flex items-center gap-2 z-20">
            {option.url && (
              <ModifyImageButtons
                project={project}
                variant="outline"
                className="opacity-0 group-hover/option:opacity-100 relative end-0 top-0"
                classNames={{
                  trigger: "bg-white",
                }}
                inputRef={inputRef}
                imageUrl={option.url}
                altText={option.altText}
                altTextPlaceholder={
                  imageMetadata[option.url]?.customMetadata?.alt
                }
                copyright={option.copyright}
                onChangeImage={(files) => handleFileChange?.(files, option.uid)}
                onRemoveImage={() => {
                  console.log("onRemoveImage", option.uid);
                  props.updateOption("", "options", option.uid, "url");
                  // removeItem(option.uid, "options", { focus: true });
                }}
                onAltTextChange={(value) => {
                  props.updateQuestion(
                    props.options?.map((e) => {
                      if (e.uid === option?.uid) {
                        return { ...e, altText: value };
                      }
                      return e;
                    }),
                    "options",
                  );
                }}
                onCopyrightChange={(value) => {
                  props.updateQuestion(
                    props.options?.map((e) => {
                      if (e.uid === option?.uid) {
                        return { ...e, copyright: value };
                      }
                      return e;
                    }),
                    "options",
                  );
                }}
                maxItems={1}
                saveToPath={`surveys/${surveyId}`}
                fileTypes={fileTypes}
              />
            )}
            {removeItem && removable && mode === "edit" && (
              <Button
                leadingIcon="x-md"
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  removeItem(option.uid, "options", { focus: true });
                }}
                className={RemoveItemButton()}
                tooltipLabel={t("util:delete", { val: t("global:option") })}
              />
            )}
          </div>
        )}
        {option ? (
          <div className="relative flex h-full w-full flex-col">
            {option?.uid === "other" ? (
              <div className="text-text-subdued relative flex aspect-square h-full w-full items-center justify-center shrink">
                <Icon name="edit" className="h-8 w-8" />
              </div>
            ) : (
              <div className="relative">
                {option.url ? (
                  <img
                    src={option.url}
                    className={ImageWrapper({
                      className: "border-none object-cover",
                    })}
                    alt={
                      option?.altText ||
                      imageMetadata[option.url]?.customMetadata?.alt ||
                      `${t("global:image")}: ${translation?.[option.value] || option.value}`
                    }
                  />
                ) : mode !== "edit" ? (
                  <div className="text-action-secondary-pressed ring-action-secondary-pressed bg-surface-highlight-subdued flex aspect-square w-full items-center justify-center rounded-lg ring-1">
                    <Icon name="add-image" className="h-8 w-8" />
                  </div>
                ) : null}
                {mode === "edit" && (
                  <FileDrop
                    onClick={() =>
                      openModal(
                        <ImagesUploadModal
                          onInputChange={(files) =>
                            handleFileChange?.(files, option.uid)
                          }
                          ai
                          stock
                          mediaBrowser
                          fileTypes={fileTypes}
                          basePath={
                            project
                              ? `organizationsData/${project.organizationId}/projects/${project.projectId}`
                              : ""
                          }
                          saveToPath={`surveys/${surveyId}`}
                          project={project}
                        />,
                        {
                          title: t("util:upload", {
                            val: t(getFileTypeLabel(1, fileTypes)),
                          }),
                          size: "xl",
                          transform: false,
                          style: { height: "80vh" },
                        },
                      )
                    }
                    ref={inputRef}
                    text={false}
                    hidden={!!option.url}
                    icon={option.url ? "download" : "add-image"}
                    className={ImageWrapper({
                      className: cn(
                        "hover:bg-surface-highlight-subdued-pressed rounded-b-none -mx-px -mt-px w-[calc(100%+2px)]",
                        option.url &&
                          "absolute inset-0 h-auto w-auto mix-blend-multiply hover:bg-white",
                      ),
                    })}
                    classNames={{
                      icon: cn("h-8 w-8 text-current"),
                    }}
                    loading={loading}
                    onDrop={(files) => handleFileChange?.(files, option.uid)}
                  />
                )}
              </div>
            )}
            <div className="flex justify-start gap-2 p-2">
              {hasConditions && mode === "edit" && (
                <AlphaNum rowIndex={rowIndex || 0} checked={checked} />
              )}
              <InvisibleInput
                id={option.uid}
                size="body-sm"
                value={translation?.[option.value] || option.value}
                onChange={({ currentTarget }) => {
                  props.updateText(currentTarget.value, option.uid, "options");
                }}
                placeholder={t("global:option")}
                onClick={(e) => e.stopPropagation()}
                classNames={{
                  container: "mt-0 pl-1",
                  input: "box-border py-[3px]",
                }}
                mode={mode}
              />
            </div>
          </div>
        ) : (
          <div className="text-text-subdued group-hover/option:text-[var(--primary-800)] group-active/option:text-[var(--primary-800)] flex h-full w-full items-center justify-center gap-1">
            <Icon name={"plus-md"} className="h-5 w-5" />
            <p>{t("global:option")}</p>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};
const ImageChoice = ({
  onChange,
  project,
  surveyId,
  ...props
}: ImageChoiceProps) => {
  const [translation] = useGlobalState().translationsState;
  const { t } = useTranslation();
  const { Outer, Wrapper, Grid } = ImageChoiceStyles();
  const type = props.multipleChoice ? "checkbox" : "radio";

  const [checked, setChecked] = useState<string | string[]>(props.value);
  const [loading, setLoading] = useState<Question["uid"][]>([]);
  const { invalidType, invalidSize, checkFileUpload } = useVerifyFiles();

  const handleFileChange = async (
    files: FileList | File[] | { url: string; alt: string }[],
    uid: string,
  ) => {
    setLoading((l) => [...new Set([...l, uid])]);

    // Handle new format (array of {image, alt} objects)
    if (Array.isArray(files) && files[0] && "url" in files[0]) {
      const imageObj = files[0] as { url: string; alt: string };
      props.updateOption(imageObj.url, "options", uid, "url");
      // props.updateOption(imageObj.alt, "options", uid, "alt");
      setLoading((l) => l.filter((e) => e !== uid));
      return;
    }

    // Handle old format (FileList or File[])
    const fileList = checkFileUpload({ files });
    const [object] = await getMediaObjects({
      fileList,
      setPath: () =>
        `organizationsData/${project.organizationId}/projects/${project.projectId}/surveys/${surveyId}/surveyImagesUpload/`,
      options: {
        metadata: {
          customMetadata: {
            lang:
              project?.settings?.originalLanguage ||
              i18n.resolvedLanguage ||
              "de",
          },
        },
      },
    });

    props.updateOption(object.url, "options", uid, "url");
    setLoading((l) => l.filter((e) => e !== uid));
  };

  const sortedOptions = useRandomSorting(props, "options", props.mode);
  const otherOption = useMemo(() => {
    return {
      uid: "other",
      value:
        props.otherTitle === undefined
          ? props.defaultOtherTitle
          : translation?.[props.otherTitle] ||
            props.otherTitle ||
            t("global:other"),
    };
  }, [props.otherTitle, props.defaultOtherTitle, translation, t]);

  const options = useMemo(() => {
    return props.mode === "display" && props.otherOption
      ? [...sortedOptions, otherOption]
      : sortedOptions;
  }, [sortedOptions, otherOption, props.mode, props.otherOption]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: (
        event,
        {
          context: { activeNodeRect, viewportDimensions, scrollableAncestors },
        },
      ) => {
        return {
          x: activeNodeRect.left,
          y: activeNodeRect.top,
        };
      },
    }),
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const handleDragStart = ({ active }) => setActiveId(active.id as string);
  const handleDragCancel = () => setActiveId(null);
  const handleDragEnd = useOnDragEnd(options, (sorted) => {
    setActiveId(null);
    props.updateQuestion(sorted, "options");
  });

  if (props.mode === "display") {
    return (
      <div className={Outer({ className: "gap-y-5" })}>
        <div className={Grid()}>
          {options.map((option) => (
            <SimpleImageChoiceItem
              key={option.uid}
              option={option}
              onChange={onChange}
              value={props.value}
              question={props}
            />
          ))}
        </div>

        {props.otherOption && isChecked(props.value, "other", type) && (
          <Textarea
            {...props}
            aria-label={otherOption.value}
            value={props.comment}
            onChange={(comment) => onChange?.({ comment })}
            textRequired={props.textResponseRequired}
            textTitle={
              props.textTitle === undefined
                ? props.defaultTextTitle
                : props.textTitle
            }
            placeholder={props.defaultTextTitle}
            maxLength={props.maxChar}
            rows={1}
          />
        )}
      </div>
    );
  }

  return (
    <div className={Outer()}>
      <div className={Wrapper()}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        >
          <SortableContext
            items={options.map((item) => item.uid)}
            strategy={rectSortingStrategy}
          >
            <div className={Grid()}>
              {options.map((option, index) => (
                <ImageChoiceItem
                  project={project}
                  surveyId={surveyId}
                  key={option.uid}
                  id={option.uid}
                  type={type}
                  option={option}
                  options={options}
                  rowIndex={index}
                  handleFileChange={handleFileChange}
                  loading={loading.includes(option.uid)}
                  {...props}
                />
              ))}
              {props.otherOption && (
                <ImageChoiceItem
                  project={project}
                  surveyId={surveyId}
                  id={otherOption.uid}
                  draggable={false}
                  type={type}
                  option={otherOption}
                  options={options}
                  rowIndex={options.length}
                  loading={loading.includes(otherOption.uid)}
                  {...props}
                />
              )}
              <ImageChoiceItem
                project={project}
                draggable={false}
                id={"add"}
                onClick={() => {
                  props.addItem("options", { focus: true });
                }}
                {...props}
              />
            </div>
          </SortableContext>

          <DragOverlay>
            <ImageChoiceItem
              project={project}
              option={options.find((option) => option.uid === activeId)}
              options={options}
              id={activeId || ""}
              rowIndex={options.findIndex((option) => option.uid === activeId)}
              handleFileChange={handleFileChange}
              {...props}
            />
          </DragOverlay>
        </DndContext>
      </div>
      {props.otherOption &&
        (props.mode === "edit" || isChecked(props.value, "other", type)) && (
          <div className="mb-5">
            <Textarea
              {...props}
              aria-label={otherOption.value}
              value={props.comment}
              onChange={(comment) => onChange?.({ comment })}
              textRequired={props.textResponseRequired}
              textTitle={
                props.textTitle === undefined
                  ? props.defaultTextTitle
                  : props.textTitle
              }
              placeholder={props.defaultTextTitle}
              maxLength={props.maxChar}
              rows={1}
            />
          </div>
        )}
    </div>
  );
};
ImageChoice.displayName = "ImageChoice";
export default ImageChoice;
